import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import definitions from "../../../common/definitions.json";
import { useApiToken } from "../../../common/apiUtils";
import SelectedItems from "../SelectedItems";
import SearchInput from "../SearchInput";
import { apiRequest, fetchTableData } from "../../../common/apiUtils";
import { CloseIcon, DropDownArrow } from "../../../assets/svgs";

const SingleDynamicSelect = React.memo((props) => {
  const { name, label, sandboxId, pageData, selectedItem, fullWidth, disabled, onChange, required } = props;

  const token = useApiToken();
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [dropdownSelection, setDropdownSelection] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [hasFetchedInitialData, setHasFetchedInitialData] = useState(false);
  const [isInitialFetchInProgress, setIsInitialFetchInProgress] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [cachedOptions, setCachedOptions] = useState([]);
  const [fetchedOptions, setFetchedOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(!!props.value?.id);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentSearchValue, setCurrentSearchValue] = useState("");

  const formatId = (id) => {
    return id.length > 20 ? `...${id.slice(-12)}` : id;
  };

  const loadFieldData = useMemo(() => {
    if (!pageData?.DynamicSelectFields || !name) return null;

    const fieldData = pageData.DynamicSelectFields[name];
    if (!fieldData) return null;

    const { tableData, responseDataName, idKey } = fieldData;

    return {
      tableData: tableData,
      responseDataName: responseDataName,
      isDynamic: !!tableData,
      idKey: idKey,
      get_endpoint: tableData
        ? definitions?.schemas[tableData]?.endpoints?.find((endpoint) => endpoint.method === "get")
        : undefined,
    };
  }, [pageData, name, definitions]);

  const PAGE_SIZE = 50;

  const fetchPage = useCallback(
    async (cursor = "0", searchValue = "") => {
      const endpoint = loadFieldData?.get_endpoint;
      if (!token || !endpoint) return null;

      const queryParams = new URLSearchParams();
      if (searchValue) queryParams.append("name", searchValue);
      queryParams.append("expand", "*");
      queryParams.append("sort_by", loadFieldData?.idKey);
      queryParams.append("sort_order", "asc");
      queryParams.append("cursor", cursor);
      queryParams.append("page_size", String(PAGE_SIZE));

      return apiRequest(token, endpoint, { sandbox_id: sandboxId }, {}, {}, { queryParams: queryParams.toString() });
    },
    [token, sandboxId, loadFieldData],
  );

  // Fetches pages in a loop until we have at least PAGE_SIZE visible
  // (filtered) results or run out of pages. Returns { allItems, cursor, hasMore }.
  const fetchUntilFull = useCallback(
    async (startCursor, searchValue, existingItems = []) => {
      let cursor = startCursor;
      let allItems = [...existingItems];
      let morePages = true;

      while (morePages) {
        const response = await fetchPage(cursor, searchValue);
        if (!response) break;

        const pageItems =
          response[loadFieldData?.responseDataName]?.map((item) => ({
            id: item[loadFieldData?.idKey],
            name: item.name || "Untitled",
          })) || [];

        // Deduplicate against existing items
        const newItems = pageItems.filter((item) => !allItems.some((existing) => existing.id === item.id));
        allItems = [...allItems, ...newItems];

        const pageMeta = response?.page_meta;
        const total = pageMeta?.total ?? 0;
        cursor = pageMeta?.cursor ?? null;
        morePages = allItems.length < total && !!cursor;

        // Apply client-side filter to check visible count
        const visibleItems = searchValue
          ? allItems.filter(
              (item) =>
                item.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
                item.id?.toLowerCase().includes(searchValue.toLowerCase()),
            )
          : allItems;

        // Stop if we have enough visible results or no more pages
        if (visibleItems.length >= PAGE_SIZE || !morePages) {
          return { allItems, visibleItems, cursor, hasMore: morePages };
        }
      }

      const visibleItems = searchValue
        ? allItems.filter(
            (item) =>
              item.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
              item.id?.toLowerCase().includes(searchValue.toLowerCase()),
          )
        : allItems;
      return { allItems, visibleItems, cursor, hasMore: false };
    },
    [fetchPage, loadFieldData],
  );

  const isQueryEnabled = useMemo(
    () => !!sandboxId && !!token && loadFieldData?.isDynamic && !!loadFieldData?.get_endpoint,
    [sandboxId, token, loadFieldData],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (props.row_key) {
        setCachedOptions([]);
      }

      if (!props.value?.id) {
        setDropdownSelection(null);
        setIsLoading(false);
        return;
      }

      if (!token) {
        // Keep isLoading true — the effect will re-fire when token arrives
        setIsLoading(true);
        return;
      }

      const endpoint = loadFieldData?.get_endpoint;
      if (!endpoint) {
        setIsLoading(false);
        return;
      }

      setIsInitialFetchInProgress(true);
      setIsLoading(true);

      try {
        // Try a direct single-item GET first (e.g. /v1/sandbox/{sandbox_id}/match-making-profile-list/{match_making_profile_list_id})
        const singleItemEndpoint = {
          path: `${endpoint.path}/{${loadFieldData?.idKey}}`,
          method: "get",
        };
        const response = await apiRequest(
          token,
          singleItemEndpoint,
          { sandbox_id: sandboxId, [loadFieldData?.idKey]: props.value.id },
        );

        const resolvedItem = {
          id: response[loadFieldData?.idKey],
          name: response.name || "Untitled",
        };
        setCachedOptions([resolvedItem]);
        setDropdownSelection(resolvedItem);
        setInputValue(resolvedItem.name || resolvedItem.id);
      } catch (error) {
        // Single-item endpoint not available — fall back to list fetch
        try {
          const response = await fetchTableData(
            token,
            loadFieldData?.idKey,
            endpoint,
            { sandbox_id: sandboxId },
            "",
            props.value,
            [],
          );

          const transformedData =
            response[loadFieldData?.responseDataName]?.map((item) => ({
              id: item[loadFieldData?.idKey],
              name: item.name || "Untitled",
            })) || [];

          setCachedOptions(transformedData);
          const matchingItem = transformedData.find((item) => item.id === props.value.id);
          if (matchingItem) {
            setDropdownSelection(matchingItem);
            setInputValue(matchingItem.name || matchingItem.id);
          } else {
            setDropdownSelection(null);
            setInputValue("");
          }
        } catch (fallbackError) {
          setFilteredOptions([]);
          setDropdownSelection(null);
          setInputValue("");
        }
      } finally {
        setIsInitialFetchInProgress(false);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [props.row_key, props.value.id, loadFieldData, token, sandboxId]);

  useEffect(() => {
    if (isDropdownOpen) {
      const fetchOptions = async () => {
        if (!token) {
          return;
        }

        try {
          const { allItems, visibleItems, cursor, hasMore: more } = await fetchUntilFull("0", "", [...cachedOptions]);
          setNextCursor(cursor);
          setHasMore(more);
          setFilteredOptions(visibleItems);
          setFetchedOptions(allItems);
        } catch (error) {
          setFilteredOptions([]);
        }
      };

      fetchOptions();
    }
  }, [isDropdownOpen, loadFieldData, token, sandboxId, cachedOptions, fetchUntilFull]);

  useEffect(() => {}, [filteredOptions]);

  const handleFocus = useCallback(() => {
    setIsDropdownOpen(true);
    setIsOpen(true);
  }, []);

  const handleSearch = useCallback(
    (searchValue) => {
      setCurrentSearchValue(searchValue);

      if (isDropdownOpen) {
        const fetchSearchResults = async () => {
          if (!token) {
            return;
          }

          const { visibleItems, cursor, hasMore: more } = await fetchUntilFull("0", searchValue, [...cachedOptions]);
          setNextCursor(cursor);
          setHasMore(more);
          setFilteredOptions(visibleItems);
        };

        fetchSearchResults();
      }
    },
    [isDropdownOpen, token, cachedOptions, fetchUntilFull],
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || !nextCursor) return;

    setIsLoadingMore(true);
    try {
      const { visibleItems, cursor, hasMore: more } = await fetchUntilFull(
        nextCursor,
        currentSearchValue,
        filteredOptions,
      );
      setNextCursor(cursor);
      setHasMore(more);
      setFilteredOptions(visibleItems);
    } catch (error) {
      console.error("Error loading more options:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, nextCursor, currentSearchValue, filteredOptions, fetchUntilFull]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollHeight - scrollTop - clientHeight < 50) {
        loadMore();
      }
    },
    [loadMore],
  );

  const toggleItem = useCallback(
    (id) => {
      const selectedItem =
        cachedOptions.find((option) => option.id === id) || filteredOptions.find((option) => option.id === id);

      setDropdownSelection(selectedItem);
      setInputValue(selectedItem ? selectedItem.name || selectedItem.id : "");
      setIsOpen(false);

      if (onChange) {
        onChange({
          target: { value: selectedItem ? selectedItem.id : null, name },
        });
      }
    },
    [cachedOptions, filteredOptions, name, onChange],
  );

  const removeItem = useCallback(
    (id) => {
      if (!id) {
        return;
      }
      setDropdownSelection(null);
      setInputValue("");

      if (onChange) {
        onChange({ target: { value: null, name } });
      }
    },
    [onChange, name],
  );

  const handleRemove = (id) => {
    if (id) {
      removeItem(id);
    }
  };

  useEffect(() => {
    if (props.value?.id && !isDropdownOpen) {
      const matchingItem = filteredOptions.find((item) => item.id === props.value.id);
      if (matchingItem) {
        setInputValue(matchingItem.name || matchingItem.id);
        setDropdownSelection(matchingItem);
      }
    }
  }, [props.value, filteredOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={dropdownRef}
      className={`relative w-full ${fullWidth ? "w-full" : ""} border ${
        disabled ? "border-zinc-600" : "border-gray-500"
      }`}
      onClick={handleFocus}
    >
      <span className="absolute top-[-1rem] left-0 text-gray-400 text-xs z-1 px-1">
        {label}
        {required && <span className="text-red-500">*</span>}
      </span>
      {isLoading ? (
        <div className="h-14 flex items-center justify-center cursor-pointer text-gray-500 animate-pulse">
          Loading...
        </div>
      ) : (
        <div className="h-14 flex items-center justify-between cursor-pointer px-3">
          <div className="flex-grow text-left overflow-hidden">
            {dropdownSelection ? (
              <span
                className={`text-gray-300 ${disabled ? "text-gray-500" : ""} block`}
                title={`${
                  dropdownSelection.name !== "Untitled" ? "Name: " + dropdownSelection.name + "\n" : ""
                }${dropdownSelection.id}`}
              >
                {dropdownSelection.name}
                <span className="text-gray-500 italic ml-1">{` (${formatId(dropdownSelection.id)})`}</span>
              </span>
            ) : props.value?.id ? (
              <span className={`text-gray-300 ${disabled ? "text-gray-500" : ""} italic block truncate`}
                title={props.value.id}
              >
                {formatId(props.value.id)}
              </span>
            ) : (
              <span className={`${disabled ? "text-gray-500" : "text-gray-300"} italic block truncate`}>
                {disabled ? `No ${label} selected` : `Please select a ${label}`}
              </span>
            )}
          </div>
          <div className="flex items-center ml-2">
            {!disabled && dropdownSelection && (
              <button onClick={() => removeItem(dropdownSelection.id)} className="mr-2">
                <CloseIcon />
              </button>
            )}
            <DropDownArrow className={`${disabled ? "text-gray-500" : "text-gray-300"}`} />
          </div>
        </div>
      )}
      {isOpen && !disabled && (
        <div className="relative">
          <ul className="absolute z-[100] w-full mt-1 bg-zinc-900 rounded-md shadow-lg border border-zinc-700">
            <li className="p-1">
              <SearchInput onSearch={handleSearch} />
            </li>
            <div ref={listRef} className="max-h-72 overflow-y-auto" onScroll={handleScroll}>
              {filteredOptions.map((option) => (
                <li
                  key={option.id}
                  className={`p-2 text-sm cursor-pointer ${
                    dropdownSelection?.id === option.id ? "bg-zinc-600" : "text-gray-300 hover:bg-zinc-600"
                  }`}
                  onClick={() => toggleItem(option.id)}
                >
                  {option.name}
                  <span className="text-gray-500 italic ml-1">{` (${formatId(option.id)})`}</span>
                </li>
              ))}
              {isLoadingMore && (
                <li className="p-2 text-sm text-gray-500 text-center animate-pulse">Loading more...</li>
              )}
            </div>
          </ul>
        </div>
      )}
    </div>
  );
});

export default SingleDynamicSelect;
